import {
  CHECKLIST_WORK_KINDS,
  type IncomingJournal,
  WORK_JOURNAL_KINDS,
  WORK_JOURNAL_WORK_KINDS,
  type WorkJournal,
  type WorkJournalEntry,
  type WorkJournalKind,
} from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { Button } from '../../ui/button';
import { EmptyState, ErrorBox, Spinner } from '../../ui/feedback';
import { Field, Input, Select } from '../../ui/form';
import { Page, PageHeader, Panel } from '../../ui/layout';
import { useProjectDefault, useProjectsWith } from '../psd/shared';

/** Дата без часового пояса: «2026-10-25» → «25.10.2026» */
const date = (v: string) => v.split('-').reverse().join('.');

type JournalKind = WorkJournalKind | 'incoming';
type Search = { project?: string; object?: string; from?: string; to?: string; kind?: JournalKind };

const JOURNALS: Record<JournalKind, string> = { ...WORK_JOURNAL_KINDS, incoming: 'Журнал входного контроля' };
const isKind = (v: unknown): v is JournalKind => typeof v === 'string' && v in JOURNALS;

/** Материалы работы одной строкой: «Бетон B25 — 3.5 м3; …» */
const materialsText = (e: WorkJournalEntry) =>
  e.materials.map((m) => `${m.name} — ${m.quantity} ${m.unit}`).join('; ');

/**
 * Журналы работ (ДК-07, ПР-09): общий и специальные (бетонные, сварочные, изоляционные) собираются из принятых отчётов
 * площадки, входного контроля — из приёмок поставок и решений ОТК склада. Править записи здесь нельзя.
 */
export function WorkJournalPage() {
  const search = useSearch({ strict: false }) as Search;
  const navigate = useNavigate();
  const { list } = useProjectsWith('documents.view');
  const projectId = search.project ?? '';
  const kind: JournalKind = isKind(search.kind) ? search.kind : 'general';
  const incoming = kind === 'incoming';
  const go = (next: Search) => navigate({ to: '/documents/journals', search: next as never, replace: true });
  useProjectDefault(list, undefined, projectId, (id) => void go({ project: id }));
  const project = list.find((p) => p.id === projectId);
  useCrumbs([{ label: 'Документы', to: '/documents' }, { label: JOURNALS[kind] }]);
  const q = useQuery({
    queryKey: ['work-journal', kind, projectId, search.object ?? '', search.from ?? '', search.to ?? ''],
    queryFn: () =>
      api<WorkJournal>('/documents/journals/work', {
        query: { projectId, objectId: search.object, from: search.from, to: search.to, kind },
      }),
    enabled: Boolean(projectId) && !incoming,
    retry: false,
  });
  const inc = useQuery({
    queryKey: ['incoming-journal', projectId, search.from ?? '', search.to ?? ''],
    queryFn: () =>
      api<IncomingJournal>('/documents/journals/incoming', {
        query: { projectId, from: search.from, to: search.to },
      }),
    enabled: Boolean(projectId) && incoming,
    retry: false,
  });
  const j = incoming ? undefined : q.data;
  const period = incoming ? inc.data : q.data;
  const pending = incoming ? inc.isPending : q.isPending;
  const error = incoming ? inc.error : q.error;
  const canPrint = Boolean(incoming ? inc.data?.entries.length : j?.entries.length);
  const objects = project?.objects ?? [];
  return (
    <Page className="max-w-6xl">
      <PageHeader
        title={JOURNALS[kind]}
        subtitle={
          incoming
            ? 'Что пришло на площадку и склад: приёмки поставок и решения входного контроля'
            : 'Что сделано на площадке: записи берутся из принятых отчётов смены'
        }
        actions={
          <div className="flex flex-wrap gap-x-5 print:hidden">
            <Link to="/documents/exec" className="inline-flex min-h-11 items-center underline">
              Исполнительная документация
            </Link>
            <Button variant="secondary" onClick={() => window.print()} disabled={!canPrint}>
              Напечатать
            </Button>
          </div>
        }
      />
      <div className="grid gap-5">
        <Panel title="Что показать" className="print:hidden">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <Field label="Журнал" htmlFor="wj-kind">
              <Select
                id="wj-kind"
                className="!h-11"
                value={kind}
                onChange={(e) =>
                  void go({
                    ...search,
                    kind: e.target.value === 'general' ? undefined : (e.target.value as JournalKind),
                  })
                }
              >
                {(Object.keys(JOURNALS) as JournalKind[]).map((k) => (
                  <option key={k} value={k}>
                    {JOURNALS[k]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Проект" htmlFor="wj-project">
              <Select
                id="wj-project"
                className="!h-11"
                value={projectId}
                onChange={(e) => void go({ project: e.target.value || undefined, kind: search.kind })}
              >
                {list.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.code} {p.name}
                  </option>
                ))}
              </Select>
            </Field>
            {incoming ? null : (
              <Field label="Объект" htmlFor="wj-object">
                <Select
                  id="wj-object"
                  className="!h-11"
                  value={search.object ?? ''}
                  onChange={(e) => void go({ ...search, object: e.target.value || undefined })}
                >
                  <option value="">Все объекты</option>
                  {objects.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
            <Field label="С даты" htmlFor="wj-from">
              <Input
                id="wj-from"
                type="date"
                className="!h-11"
                value={search.from ?? period?.from ?? ''}
                onChange={(e) => void go({ ...search, from: e.target.value || undefined })}
              />
            </Field>
            <Field label="По дату" htmlFor="wj-to">
              <Input
                id="wj-to"
                type="date"
                className="!h-11"
                value={search.to ?? period?.to ?? ''}
                onChange={(e) => void go({ ...search, to: e.target.value || undefined })}
              />
            </Field>
          </div>
        </Panel>
        {pending && projectId ? <Spinner /> : null}
        {error ? <ErrorBox error={error} /> : null}
        {incoming && inc.data ? <IncomingTable j={inc.data} /> : null}
        {j ? (
          <Panel title={`Журнал: ${j.projectCode} ${j.projectName}`} meta={`${date(j.from)} — ${date(j.to)}`}>
            <p className="mb-3 text-sm text-ink-2">
              Принятых отчётов: {j.reportsCount}. Дней без записей: {j.daysWithoutEntries}.
              {j.pendingReports
                ? ` Ещё на проверке: ${j.pendingReports} — в журнал попадут после принятия.`
                : ''}
            </p>
            {j.kind !== 'general' ? (
              <p className="mb-3 text-sm text-ink-2 print:hidden">
                В журнал идут работы, к которым применён шаблон исполнительной документации вида «
                {WORK_JOURNAL_WORK_KINDS[j.kind]
                  .map((k) => CHECKLIST_WORK_KINDS[k as keyof typeof CHECKLIST_WORK_KINDS] ?? k)
                  .join(', ')}
                ». Таких работ в проекте: {j.kindActivities ?? 0}.{' '}
                <Link
                  to="/documents/exec"
                  search={{ project: j.projectId } as never}
                  className="inline-flex min-h-11 items-center underline"
                >
                  Применить шаблон к работе
                </Link>
              </p>
            ) : null}
            {j.entries.length ? (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[56rem] border-collapse text-left text-sm">
                    <thead>
                      <tr className="border-b border-line text-ink-2">
                        <th className="p-2">№</th>
                        <th className="p-2">Дата, смена</th>
                        <th className="p-2">Вид работ</th>
                        <th className="p-2">Объём</th>
                        <th className="p-2">Люди / техника / фото</th>
                        <th className="p-2">Участок, исполнитель</th>
                        <th className="p-2">Примечание</th>
                      </tr>
                    </thead>
                    <tbody>
                      {j.entries.map((e) => (
                        <tr key={`${e.reportId}-${e.lineId}`} className="border-b border-line align-top">
                          <td className="p-2">{e.no}</td>
                          <td className="p-2">
                            {date(e.day)}, {e.shift}
                          </td>
                          <td className="p-2 [overflow-wrap:anywhere]">
                            {e.activityName}
                            {e.objectName ? <span className="block text-ink-2">{e.objectName}</span> : null}
                          </td>
                          <td className="p-2">
                            {e.quantity} {e.unit}
                            {e.claimed !== e.quantity ? (
                              <span className="block text-ink-2">заявлено {e.claimed}</span>
                            ) : null}
                            {e.materials.length ? (
                              <span className="block text-ink-2 [overflow-wrap:anywhere]">
                                {materialsText(e)}
                              </span>
                            ) : null}
                          </td>
                          <td className="p-2">
                            {e.people} / {e.machines} / {e.photos}
                          </td>
                          <td className="p-2">
                            {e.packageName}
                            <span className="block text-ink-2">{e.authorName}</span>
                          </td>
                          <td className="p-2 [overflow-wrap:anywhere]">{e.note ?? ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <ul className="grid gap-3 md:hidden">
                  {j.entries.map((e) => (
                    <li key={`${e.reportId}-${e.lineId}`} className="rounded-md border border-line p-3">
                      <p className="font-medium [overflow-wrap:anywhere]">
                        {e.no}. {e.activityName}
                      </p>
                      <p className="mt-1 text-sm">
                        {e.quantity} {e.unit}
                        {e.claimed !== e.quantity ? ` (заявлено ${e.claimed})` : ''}
                      </p>
                      <p className="mt-1 text-sm text-ink-2">
                        {date(e.day)}, {e.shift} · {e.packageName} · {e.authorName}
                      </p>
                      <p className="text-sm text-ink-2">
                        Людей: {e.people} · техники: {e.machines} · фото: {e.photos}
                      </p>
                      {e.materials.length ? (
                        <p className="text-sm text-ink-2 [overflow-wrap:anywhere]">
                          Материалы: {materialsText(e)}
                        </p>
                      ) : null}
                      {e.note ? <p className="mt-1 text-sm [overflow-wrap:anywhere]">{e.note}</p> : null}
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <EmptyState
                title={
                  j.kind === 'general'
                    ? 'За выбранный период принятых отчётов смены нет.'
                    : 'За выбранный период принятых работ этого вида нет.'
                }
              />
            )}
          </Panel>
        ) : null}
      </div>
    </Page>
  );
}

/** Журнал входного контроля: приёмки поставок на площадке и решения ОТК по приходам склада */
function IncomingTable({ j }: { j: IncomingJournal }) {
  return (
    <Panel title={`Журнал: ${j.projectCode} ${j.projectName}`} meta={`${date(j.from)} — ${date(j.to)}`}>
      {j.truncated ? (
        <p className="mb-3 text-sm text-ink-2">
          Строк входного контроля склада слишком много: показаны последние, более ранние строки за этот период
          могут не войти. Полный список — на странице склада.
        </p>
      ) : null}
      {j.entries.length ? (
        <>
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full min-w-[56rem] border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-line text-ink-2">
                  <th className="p-2">№</th>
                  <th className="p-2">Дата</th>
                  <th className="p-2">Материал</th>
                  <th className="p-2">Количество</th>
                  <th className="p-2">Поставщик, договор</th>
                  <th className="p-2">Документ</th>
                  <th className="p-2">Качество</th>
                  <th className="p-2">Результат</th>
                </tr>
              </thead>
              <tbody>
                {j.entries.map((e) => (
                  <tr key={`${e.source}-${e.id}`} className="border-b border-line align-top">
                    <td className="p-2">{e.no}</td>
                    <td className="p-2">
                      {date(e.day)}
                      <span className="block text-ink-2">{e.source === 'site' ? 'площадка' : 'склад'}</span>
                    </td>
                    <td className="p-2 [overflow-wrap:anywhere]">{e.material}</td>
                    <td className="p-2">
                      {e.quantity} {e.unit ?? ''}
                    </td>
                    <td className="p-2 [overflow-wrap:anywhere]">
                      {e.supplierName ?? '—'}
                      {e.contractNumber && e.contractId ? (
                        <Link
                          to="/contracts/$contractId"
                          params={{ contractId: e.contractId }}
                          className="flex min-h-11 items-center text-ink-2 underline"
                        >
                          договор {e.contractNumber}
                        </Link>
                      ) : null}
                    </td>
                    <td className="p-2 [overflow-wrap:anywhere]">{e.document ?? '—'}</td>
                    <td className="p-2 [overflow-wrap:anywhere]">{e.quality}</td>
                    <td className="p-2 [overflow-wrap:anywhere]">
                      {e.result}
                      {e.remarkId ? (
                        <Link
                          to="/supervision/remarks/$remarkId"
                          params={{ remarkId: e.remarkId }}
                          className="flex min-h-11 items-center underline"
                        >
                          Замечание поставщику
                        </Link>
                      ) : null}
                      {e.personName ? <span className="block text-ink-2">{e.personName}</span> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ul className="grid gap-3 md:hidden">
            {j.entries.map((e) => (
              <li key={`${e.source}-${e.id}`} className="rounded-md border border-line p-3">
                <p className="font-medium [overflow-wrap:anywhere]">
                  {e.no}. {e.material}
                </p>
                <p className="mt-1 text-sm">
                  {e.quantity} {e.unit ?? ''} · {date(e.day)} · {e.source === 'site' ? 'площадка' : 'склад'}
                </p>
                <p className="mt-1 text-sm text-ink-2 [overflow-wrap:anywhere]">
                  {e.supplierName ?? 'Поставщик не указан'}
                  {e.contractNumber && !e.contractId ? ` · договор ${e.contractNumber}` : ''}
                  {e.document ? ` · ${e.document}` : ''}
                </p>
                <p className="text-sm text-ink-2 [overflow-wrap:anywhere]">{e.quality}</p>
                <p className="mt-1 text-sm [overflow-wrap:anywhere]">
                  {e.result}
                  {e.personName ? ` · ${e.personName}` : ''}
                </p>
                <div className="flex flex-wrap gap-x-4">
                  {e.contractNumber && e.contractId ? (
                    <Link
                      to="/contracts/$contractId"
                      params={{ contractId: e.contractId }}
                      className="inline-flex min-h-11 items-center text-sm underline"
                    >
                      Договор {e.contractNumber}
                    </Link>
                  ) : null}
                  {e.remarkId ? (
                    <Link
                      to="/supervision/remarks/$remarkId"
                      params={{ remarkId: e.remarkId }}
                      className="inline-flex min-h-11 items-center text-sm underline"
                    >
                      Замечание поставщику
                    </Link>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <EmptyState title="За выбранный период приёмок и входного контроля нет." />
      )}
    </Panel>
  );
}
